import { z } from 'zod';
import { CreateProductInput, ProductIdSchema, TenantIdSchema } from './schemas';
import { IMPORT_MAX_ROWS } from './productImport';

/**
 * Contratos das escritas de produto pelo SERVIDOR — criar/editar, arquivar e
 * importar planilha — usados pelo app web e pela tela do cliente do CRM.
 *
 * Origem: #776 — 2026-09-29 13:11 +0200 (owner: "no CRM … aba produtos deve
 * poder criar produto individual ou importar planilha como já tem no app
 * web"; e o app web passa a usar as mesmas callables — uma fonte só).
 *
 * Por quê: o app web gravava direto no Firestore (rules de manager) e o CRM
 * não é membro do tenant. Pelo servidor as duas telas escrevem igual, e as
 * regras que só existiam no cliente — teto de `MAX_PRODUCTS_PER_TENANT` e
 * dedupe de nome na importação — passam a valer de verdade.
 *
 * Contrato: `ProductFieldsSchema` é o formulário do produto (os mesmos campos
 * que `apps/app/src/lib/firestoreProducts.ts` gravava), derivado de
 * `CreateProductInput` por `.pick` — se o contrato apertar um limite, este
 * aperta junto. `barcode` vazio = sem código (o servidor grava `null`).
 * `supplierIds` ausente na edição = não mexe (o CRM não tem o seletor).
 *
 * Usado por: callables `upsertProduct`, `setProductActive`, `importProducts`
 * (`apps/functions/src/products/`); `apps/app` (`firestoreProducts.ts`,
 * importação) e CRM (`ProductEditorPage`, `ProductImportPage`).
 *
 * Issues: #776.
 */
export const ProductFieldsSchema = CreateProductInput.pick({
  name: true,
  description: true,
  category: true,
  sku: true,
  ingredients: true,
  allergens: true,
  unit: true,
  validityRules: true,
}).extend({
  name: z.string().trim().min(1).max(120),
  barcode: z.string().trim().max(60).default(''),
  supplierIds: z.array(z.string().min(1).max(128)).max(20).optional(),
});
export type ProductFields = z.input<typeof ProductFieldsSchema>;

export const UpsertProductInput = z.object({
  tenantId: TenantIdSchema,
  /** Ausente = criar. */
  productId: ProductIdSchema.optional(),
  fields: ProductFieldsSchema,
  /** Só na criação (editar o mínimo segue no fluxo de estoque). */
  minStock: z.number().int().min(0).max(1_000_000).nullable().optional(),
});
export type UpsertProductInput = z.input<typeof UpsertProductInput>;
export const UpsertProductOutput = z.object({ productId: z.string() });
export type UpsertProductOutput = z.infer<typeof UpsertProductOutput>;

export const SetProductActiveInput = z.object({
  tenantId: TenantIdSchema,
  productId: ProductIdSchema,
  active: z.boolean(),
});
export type SetProductActiveInput = z.input<typeof SetProductActiveInput>;

/**
 * Lote de linhas JÁ VALIDADAS pela revisão (`parseSheet`) — o servidor
 * revalida cada uma pelo schema, deduplica contra o catálogo ATUAL e dentro
 * do lote, e aplica o teto. Demote-only, como no cliente: uma linha nunca
 * vira "criada" se a revisão não a mostrou pronta.
 */
export const ImportProductRowSchema = z.object({
  rowNumber: z.number().int().min(2),
  fields: ProductFieldsSchema,
  minStock: z.number().int().min(0).max(1_000_000).nullable(),
});
export const ImportProductsInput = z.object({
  tenantId: TenantIdSchema,
  rows: z.array(ImportProductRowSchema).min(1).max(IMPORT_MAX_ROWS),
});
export type ImportProductsInput = z.input<typeof ImportProductsInput>;
/**
 * O mesmo envelope, com cada linha ainda crua — o servidor valida LINHA A
 * LINHA (`ImportProductRowSchema`) para uma linha inválida virar `INVALID`
 * sem derrubar as outras. Os clientes usam `ImportProductsInput` (tipado).
 */
export const ImportProductsWireInput = z.object({
  tenantId: TenantIdSchema,
  rows: z.array(z.looseObject({ rowNumber: z.number().int() })).min(1).max(IMPORT_MAX_ROWS),
});

export const ImportRowResultStatus = {
  CREATED: 'CREATED',
  DUPLICATE_CATALOG: 'DUPLICATE_CATALOG',
  DUPLICATE_SHEET: 'DUPLICATE_SHEET',
  LIMIT_REACHED: 'LIMIT_REACHED',
  INVALID: 'INVALID',
  FAILED: 'FAILED',
} as const;
export type ImportRowResultStatus = (typeof ImportRowResultStatus)[keyof typeof ImportRowResultStatus];

export const ImportProductsOutput = z.object({
  results: z.array(
    z.object({
      rowNumber: z.number().int(),
      status: z.enum(Object.values(ImportRowResultStatus) as [ImportRowResultStatus, ...ImportRowResultStatus[]]),
      productId: z.string().optional(),
    }),
  ),
});
export type ImportProductsOutput = z.infer<typeof ImportProductsOutput>;
